import * as React from 'react';
import { useEffect, useRef, useState } from 'react';
import { makeStyles, tokens, Button, Spinner } from '@fluentui/react-components';
import { FocusTrapZone } from '@fluentui/react';
import { ServiceContext } from '@msx/platform-services';

// Import centralized interfaces
import type { FormField, FormMessageProps } from '../interfaces';
import { postExternalConsumptionAPI, getExternalConsumptionAPI } from '../../../utils/httpUtilsExternalAPI';

const useStyles = makeStyles({
  formContainer: {
    width: '100%',
    // minHeight: '200px',   
    borderRadius: '8px',
    backgroundColor: tokens.colorNeutralBackground1,
    overflow: 'visible',
    position: 'static', 
    display: 'block', 
    contain: 'none', 
    padding:'10px',
    
    // Ensure proper focus styling for form elements
    '& input, & textarea, & select': {
      '&:focus': {
        outline: `2px solid ${tokens.colorCompoundBrandStroke}`,
        outlineOffset: '1px',
      }
    },
    
    '& button': {
      '&:focus': {
        outline: `2px solid ${tokens.colorCompoundBrandStroke}`,
        outlineOffset: '1px',
      }
    }
  },
  loadingOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(255, 255, 255, 0.8)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1,
    pointerEvents: 'none' 
  },
  errorMessage: {
    padding: '16px',
    color: tokens.colorPaletteRedForeground1,
    backgroundColor: tokens.colorPaletteRedBackground1,
    borderRadius: '4px',
    margin: '8px',
    fontSize: '14px'
  },
  fallbackContainer: {
    padding: '16px',
    textAlign: 'center',
  },
  webComponent: {
    width: '100%',
    maxWidth: '100%',
    minWidth: 0,
    minHeight: '200px',
    display: 'block',
    position: 'static',
    zIndex: 'auto',
    isolation: 'auto',
    boxSizing: 'border-box',
    overflow: 'visible' 
  },
  retryButton: {
    marginTop: '12px'
  }
});

declare global {
  namespace JSX {
    interface IntrinsicElements {
      'dynamic-copilot-form-render': any;
    }
  }
}
  // Note: copilot_render.v1.0.6.js (follows semantic versioning: MAJOR.MINOR.PATCH).
  // Bump version here when copilot_render is updated — also rename the file in /public.
  // This ensures browser cache is busted on each release (same pattern as form-render in DynamicForm.tsx).
  // v1.0.3: bumped to force re-registration of the conditionalHint-bearing web component
  // (the v1.0.2 filename collision left stale custom-element registrations in browsers).
  // v1.0.4: fixed Compete radio-field alignment (Bug #1278328) — kept the native
  // fieldset group box (border/legend) identical to UAT, and fixed the Yes/No
  // vertical misalignment by using a centered flex radio-group and removing the
  // asymmetric fluent-radio margin-bottom. Rename also cache-busts the CDN.
  // v1.0.6: fixed date validation (Bug #1296181, #1296195) — the future-date
  // check in validateField now respects the per-field `futureDatesOnly` flag
  // (matching the renderer). Date fields without the flag (e.g. Project Management
  // "What period is this related to?", "period/date of the hours", "When did this
  // issue occur?") now accept current and past dates instead of forcing future-only.
const copilot_render_file_name = "copilot_render.v1.0.6.js";
const FormMessage: React.FC<FormMessageProps> = ({
  fields,
  formTitle = 'Dynamic Form',
  formDescription = '', 
  onFormSubmit,
  onShowNextSection,
  apiJsonResponse,
  persistentFormValues = {},
  isActiveForm = true
}) => {

  // Extract formJson data if available (new API format)
  const formJson = apiJsonResponse?.formJson;
  const styles = useStyles();
  const formRef = useRef<HTMLElement>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [webComponentLoaded, setWebComponentLoaded] = useState(false);
  const [componentKey, setComponentKey] = useState(Date.now()); 
  const platformContext = React.useContext(ServiceContext);
  // 🔥 Story 1249086: Track if pre-fill has already been applied to prevent
  // re-application when useEffect re-fires (e.g., onFormSubmit ref change after submit)
  const prefillAppliedRef = useRef(false);
  // Track last form context to detect when form/subsection/collectedData changes
  const lastFormContextRef = useRef<string | null>(null);

  // 🔥 FIX: Memoize collectedData JSON for stable dependency comparison
  const collectedDataJson = React.useMemo(
    () => JSON.stringify(formJson?.collectedData || {}),
    [formJson?.collectedData]
  );

  // Reset prefillAppliedRef when form context changes (subsection or collectedData)
  // This ensures pre-fill applies correctly after page refresh or navigation
  // Note: Backend sends 'subsectionId', not 'sessionId' or 'currentSubsectionKey'
  useEffect(() => {
    const currentContext = `${formJson?.subsectionId || ''}_${collectedDataJson}`;
    if (lastFormContextRef.current !== currentContext) {
      prefillAppliedRef.current = false; // Reset so pre-fill can apply to new form
      lastFormContextRef.current = currentContext;
    }
  }, [formJson?.subsectionId, collectedDataJson]);

  // Use same environment variables as DynamicForm.tsx
  const RESOURCE_EXT = `${process.env.REACT_APP_EXT_API_RESOURCE}`;
  const RESOURCE_INT = `${process.env.REACT_APP_API_RESOURCE}`;
  const EXT_BASE_URL = `${process.env.REACT_APP_EXT_API_BASE_URL}`;
  const INT_BASE_URL = `${process.env.REACT_APP_Consumption_API_ENDPOINT}`;
  const BLOB_BASE_URL = process.env.REACT_APP_BLOBSASSTART;

  // Create apiConfig object exactly like DynamicForm.tsx
  const apiConfig = {
    RESOURCE_EXT,
    EXT_BASE_URL,
    RESOURCE_INT,
    INT_BASE_URL,
    BLOB_BASE_URL
  };

  // Check if the web component bundle is loaded
  useEffect(() => {
    // Reset states when componentKey changes (after refresh)
    setIsLoading(true);
    setError(null);
    setWebComponentLoaded(false);
    
    const checkWebComponent = () => {
      const isLoaded = !!(window as any).__COPILOT_WC_BUNDLE_LOADED__ && 
                     customElements.get('dynamic-copilot-form-render');
      
      if (isLoaded) {
        setWebComponentLoaded(true);
        setIsLoading(false);
        setError(null);
      } else {
        // Try to load the copilot_render script if not already loaded
        loadCopilotRenderScript();
      }
    };

    checkWebComponent();
  }, [componentKey]); // Re-run when componentKey changes

  const loadCopilotRenderScript = () => {
    // Check if script is already loaded
   
    if (document.querySelector(`script[src$="/${copilot_render_file_name}"]`)) {
      // Script exists but component might not be ready yet
      setTimeout(checkWebComponentReadiness, 100);
      return;
    }

    // Patch customElements.define to skip duplicate registrations silently.
    // copilot_render now uses 'dynamic-copilot-form-builder' (unique name) so no conflict
    // with form-render's 'dynamic-form-builder'. Still guard against any other duplicates.
    const originalDefine = customElements.define.bind(customElements);
    (customElements as any).define = function (name: string, ctor: CustomElementConstructor, options?: ElementDefinitionOptions) {
      if (customElements.get(name)) {
        // Skip any duplicate registrations silently
        return;
      }
      originalDefine(name, ctor, options);
    };

    const script = document.createElement('script');
    // Use same pattern as DynamicForm.tsx with PUBLIC_URL for consistency
    script.src = `${process.env.PUBLIC_URL}/${copilot_render_file_name}`;
    script.type = 'module';
    
    script.onload = () => {
      (customElements as any).define = originalDefine;
      setTimeout(checkWebComponentReadiness, 100);
    };
    
    script.onerror = () => {
      (customElements as any).define = originalDefine;
      setError('Please refresh the page.');
      setIsLoading(false);
    };

    document.head.appendChild(script);
  };

  const checkWebComponentReadiness = () => {
    const maxAttempts = 50; // 5 seconds max
    let attempts = 0;

    const check = () => {
      attempts++;
      const isReady = !!(window as any).__COPILOT_WC_BUNDLE_LOADED__ && 
                     customElements.get('dynamic-copilot-form-render');
      
      if (isReady) {
        setWebComponentLoaded(true);
        setIsLoading(false);
        setError(null);
      } else if (attempts < maxAttempts) {
        setTimeout(check, 100);
      } else {
        setError('Form components failed to load. Please refresh the page.');
        setIsLoading(false);
      }
    };

    check();
  };

  // Set up form fields and event listeners when component is ready - following DynamicForm.tsx pattern exactly
  useEffect(() => {
    const checkElementAndSetProperties = () => {
      // Generate unique ID for each instance to avoid DOM conflicts after refresh
      const elementId = `dynamicCopilotFormRender_${componentKey}`;
      const formElement = document.getElementById(elementId) as HTMLElement;

      if (formElement && webComponentLoaded && fields && platformContext) {
        // Clear any existing listeners to prevent duplicates
        if ((formElement as any)._listenersAdded) {
          // Remove existing event listeners
          const events = ['copilot-form-submit', 'copilot-show-next-section'];
          events.forEach(eventName => {
            const listeners = (formElement as any)[`_${eventName}_listeners`] || [];
            listeners.forEach((listener: any) => {
              formElement.removeEventListener(eventName, listener);
            });
          });
          (formElement as any)._listenersAdded = false;
        }
        
        // Force single-column layout in copilot panel via CSS custom property.
        // CSS custom properties inherit through shadow DOM boundaries, so this
        // reliably overrides .form-group width without needing shadow DOM injection timing.
        formElement.style.setProperty('--form-group-width', '100%');

        // Also inject into shadow DOM as a fallback for older form-render versions
        const injectCopilotWidthFix = () => {
          const outerShadow = formElement.shadowRoot;
          if (!outerShadow) return;
          const formBuilder = outerShadow.querySelector('dynamic-copilot-form-builder') ||
                              outerShadow.querySelector('dynamic-form-builder') ||
                              outerShadow.querySelector('dynamic-form-builder-standalone');
          if (!formBuilder) return;
          const innerShadow = formBuilder.shadowRoot;
          if (!innerShadow) return;
          if (innerShadow.querySelector('#copilot-form-group-fix')) return; // already injected
          const style = document.createElement('style');
          style.id = 'copilot-form-group-fix';
          style.textContent = `
            .form-group,
            .form-group.full-width,
            .form-group.full-width-container {
              width: 100% !important;
            }
          `;
          innerShadow.appendChild(style);
        };
        // Retry injection — shadow DOM may not be ready immediately
        injectCopilotWidthFix();
        requestAnimationFrame(injectCopilotWidthFix);
        setTimeout(injectCopilotWidthFix, 300);
        setTimeout(injectCopilotWidthFix, 800);

        // ============================================================================
        // WI 1271833: AI-powered blocker description validation
        // Inject a "Validate with AI" button + informational (non-blocking) banner
        // under any field that carries an `aiValidation` config. The copilot form is
        // rendered by the copilot_render web component (built artifact, source not in
        // this repo), so — like the cascade/width fixes above — we inject into its
        // shadow DOM. The button calls the external API validation endpoint and shows
        // blue "tips" / green "ready" / red "unavailable" feedback. Submit is never
        // blocked.
        // ============================================================================
        const injectAiValidation = () => {
          const aiFields = (fields || []).filter((f: any) => f?.aiValidation?.enabled);
          if (aiFields.length === 0) return;

          const outerShadow = formElement.shadowRoot;
          if (!outerShadow) return;
          const formBuilder = outerShadow.querySelector('dynamic-copilot-form-builder') ||
                              outerShadow.querySelector('dynamic-form-builder') ||
                              outerShadow.querySelector('dynamic-form-builder-standalone');
          if (!formBuilder) return;
          const innerShadow: ShadowRoot | null = (formBuilder as any).shadowRoot;
          if (!innerShadow) return;

          const inputSelector = 'fluent-text-field, fluent-text-area, fluent-select, fluent-combobox, input, textarea, select';

          // Find a rendered input element by field id/name (case-insensitive)
          const findInput = (fieldId: string, preferTextLike = false): any => {
            const all = Array.from(innerShadow.querySelectorAll(inputSelector)) as any[];
            const target = fieldId.toLowerCase();
            const matches = all.filter((cand) => {
              const id = (cand.id || '').toLowerCase();
              const nm = (cand.getAttribute?.('name') || cand.name || '').toLowerCase();
              return id === target || nm === target;
            });
            if (matches.length === 0) return null;
            if (preferTextLike) {
              const tl = matches.find((m) => {
                const tag = (m.tagName || '').toLowerCase();
                return tag === 'fluent-text-area' || tag === 'textarea' || tag === 'fluent-text-field';
              });
              if (tl) return tl;
            }
            return matches[0];
          };

          // Read a field's current value from the DOM, falling back to formValues
          const readValue = (fieldId: string): string => {
            const el = findInput(fieldId);
            if (el && el.value != null && el.value !== '') return String(el.value);
            const fv = (formBuilder as any).formValues || {};
            return fv[fieldId] != null ? String(fv[fieldId]) : (el && el.value != null ? String(el.value) : '');
          };

          for (const field of aiFields) {
            const cfg = (field as any).aiValidation;
            const targetInput = findInput(field.id, true);
            if (!targetInput) continue;

            const container: HTMLElement | null =
              (targetInput.closest && targetInput.closest('.form-group')) || targetInput.parentElement;
            if (!container) continue;

            const markerId = `ai-validate-wrap-${field.id}`;
            if (innerShadow.querySelector(`#${(window as any).CSS?.escape ? CSS.escape(markerId) : markerId}`)) {
              continue; // already injected for this field
            }

            const wrap = document.createElement('div');
            wrap.id = markerId;
            wrap.style.cssText = 'margin:6px 0 10px 0; display:flex; flex-direction:column; gap:8px;';

            const btn = document.createElement('button');
            btn.type = 'button';
            btn.textContent = cfg.buttonText || '✨ Validate with AI';
            btn.style.cssText =
              'align-self:flex-start; cursor:pointer; padding:6px 14px; border-radius:4px; ' +
              'border:1px solid #0078D4; background:#ffffff; color:#0078D4; font-size:13px; font-weight:600;';

            const inlineMsg = document.createElement('div');
            inlineMsg.setAttribute('role', 'alert');
            inlineMsg.style.cssText = 'display:none; font-size:12px; color:#605E5C;';

            const banner = document.createElement('div');
            banner.setAttribute('role', 'status');
            banner.setAttribute('aria-live', 'polite');
            banner.style.cssText = 'display:none; padding:10px 12px; border-radius:4px; font-size:13px; line-height:1.4;';

            wrap.appendChild(btn);
            wrap.appendChild(inlineMsg);
            wrap.appendChild(banner);
            container.insertAdjacentElement('afterend', wrap);

            const showInline = (text: string) => {
              inlineMsg.textContent = text || '';
              inlineMsg.style.display = text ? 'block' : 'none';
            };
            const hideBanner = () => { banner.style.display = 'none'; };
            const showBanner = (kind: 'tips' | 'success' | 'error' | 'loading', title: string, bullets?: string[] | null) => {
              const palette: Record<string, { bg: string; border: string }> = {
                tips: { bg: '#EFF6FC', border: '#0078D4' },
                success: { bg: '#F1FAF1', border: '#107C10' },
                error: { bg: '#FDF3F4', border: '#A4262C' },
                loading: { bg: '#F3F2F1', border: '#8A8886' },
              };
              const p = palette[kind];
              banner.style.background = p.bg;
              banner.style.borderLeft = `4px solid ${p.border}`;
              banner.style.color = '#201F1E';
              banner.style.display = 'block';
              banner.textContent = '';
              if (title) {
                const t = document.createElement('div');
                t.style.fontWeight = '600';
                t.style.marginBottom = bullets && bullets.length ? '6px' : '0';
                t.textContent = title;
                banner.appendChild(t);
              }
              if (bullets && bullets.length) {
                const ul = document.createElement('ul');
                ul.style.cssText = 'margin:0; padding-left:18px;';
                bullets.slice(0, 3).forEach((b) => {
                  const li = document.createElement('li');
                  li.textContent = b;
                  ul.appendChild(li);
                });
                banner.appendChild(ul);
              }
            };

            btn.addEventListener('click', async () => {
              showInline('');
              const description = readValue(field.id).trim();
              if (!description) {
                hideBanner();
                showInline('Please enter a description before validating.');
                return;
              }
              const prevText = btn.textContent;
              btn.disabled = true;
              btn.textContent = 'Validating…';
              showBanner('loading', 'Validating…');
              try {
                const payload: Record<string, any> = { description };
                const ctxFields: string[] = Array.isArray(cfg.contextFields) ? cfg.contextFields : [];
                for (const cf of ctxFields) {
                  payload[cf] = readValue(cf);
                }
                const endpoint = cfg.endpoint || '/api/compete/validate-blocker';
                const resp = await postExternalConsumptionAPI(endpoint, payload, (platformContext as any).authClient);
                const data: any = resp?.data ?? resp;
                if (!data || typeof data !== 'object' || (!data.status && !data.feedback && !Array.isArray(data.bullets))) {
                  throw new Error('Invalid validation response');
                }
                const status = String(data.status || '').toLowerCase();
                const bullets: string[] = Array.isArray(data.bullets) ? data.bullets : [];
                if (status === 'ready') {
                  showBanner('success', data.feedback || 'Your description is clear and ready to submit.', bullets.length ? bullets : null);
                } else if (status === 'empty') {
                  hideBanner();
                  showInline('Please enter a description before validating.');
                } else {
                  showBanner('tips', data.feedback || 'Here are a few suggestions to strengthen your description:', bullets);
                }
              } catch (err) {
                showBanner('error', 'Validation is temporarily unavailable. You can still submit your request.');
              } finally {
                btn.disabled = false;
                btn.textContent = prevText;
              }
            });
          }
        };
        // Re-run with the same cadence as the width fix — the inner shadow DOM renders
        // asynchronously and may re-render on cascade. Injection is idempotent.
        injectAiValidation();
        requestAnimationFrame(injectAiValidation);
        setTimeout(injectAiValidation, 300);
        setTimeout(injectAiValidation, 800);
        setTimeout(injectAiValidation, 1500);

        // ============================================================================
        // WI 1278277: Opportunity ID searchable dropdown (typeahead)
        // The copilot form is rendered by the external copilot_render web component,
        // which has no native async searchable-dropdown control. So — exactly like the
        // AI-validation button above — we enhance the field's text input via shadow-DOM
        // injection: a debounced typeahead that queries the backend and shows a suggestion
        // list. On error it silently leaves the plain text input (free-text fallback), so
        // ticket creation is never blocked. Injection is idempotent.
        // ============================================================================
        const injectOpportunitySearch = () => {
          const dsFields = (fields || []).filter((f: any) => f?.dataSource?.endpoint);
          if (dsFields.length === 0) return;

          const outerShadow = formElement.shadowRoot;
          if (!outerShadow) return;
          const formBuilder = outerShadow.querySelector('dynamic-copilot-form-builder') ||
                              outerShadow.querySelector('dynamic-form-builder') ||
                              outerShadow.querySelector('dynamic-form-builder-standalone');
          if (!formBuilder) return;
          const innerShadow: ShadowRoot | null = (formBuilder as any).shadowRoot;
          if (!innerShadow) return;

          const inputSelector = 'fluent-text-field, fluent-combobox, input';
          const findInput = (fieldId: string): any => {
            const all = Array.from(innerShadow.querySelectorAll(inputSelector)) as any[];
            const target = fieldId.toLowerCase();
            return all.find((cand) => {
              const id = (cand.id || '').toLowerCase();
              const nm = (cand.getAttribute?.('name') || cand.name || '').toLowerCase();
              return id === target || nm === target;
            }) || null;
          };

          // Push the selected value into both the DOM input and the web component's
          // formValues bag, then fire input/change so the component captures it.
          const commitValue = (input: any, fieldId: string, value: string) => {
            try {
              input.value = value;
              const nativeInput = input.shadowRoot?.querySelector('input') || input;
              if (nativeInput && nativeInput !== input) nativeInput.value = value;
              const fv = (formBuilder as any).formValues || {};
              fv[fieldId] = value;
              (formBuilder as any).formValues = fv;
              input.dispatchEvent(new Event('input', { bubbles: true, composed: true }));
              input.dispatchEvent(new Event('change', { bubbles: true, composed: true }));
            } catch { /* best-effort */ }
          };

          for (const field of dsFields) {
            const cfg = (field as any).dataSource;
            const input = findInput(field.id);
            if (!input) continue;

            // Feedback: when a searchable field is PREFILLED, render it as a plain textbox — no
            // typeahead/dropdown. Only unfilled fields get the search enhancement. We tear down any
            // prior enhancement (chip/panel, wherever it was nested) and show the input carrying its
            // prefilled value.
            const prefilledSet: Set<string> | undefined = (formBuilder as any).__oppPrefilled;
            if (prefilledSet && prefilledSet.has(field.id.toLowerCase())) {
              // Drop any tracked selection so nothing re-renders a chip for this field.
              const selStoreAll: Record<string, any> = (formBuilder as any).__oppSelections || {};
              delete selStoreAll[field.id];
              // Remove chip host / hide panel from BOTH candidate containers (the .form-group and the
              // input's direct parent may differ) and search descendants, not just direct children.
              const scopes: HTMLElement[] = [];
              const fg = input.closest && (input.closest('.form-group') as HTMLElement | null);
              if (fg) scopes.push(fg);
              if (input.parentElement && !scopes.includes(input.parentElement)) scopes.push(input.parentElement);
              for (const sc of scopes) {
                sc.querySelectorAll('.opp-chip-host').forEach((c) => c.remove());
                sc.querySelectorAll('.opp-suggest-panel').forEach((p) => { (p as HTMLElement).style.display = 'none'; });
              }
              input.style.display = '';
              const pv = String(((formBuilder as any).formValues || {})[field.id] ?? '');
              if (pv && String((input as any).value || '') !== pv) (input as any).value = pv;
              continue;
            }

            const minChars = typeof cfg.minChars === 'number' ? cfg.minChars : 3;
            const debounceMs = typeof cfg.debounceMs === 'number' ? cfg.debounceMs : 300;
            const searchParam = cfg.searchParam || 'search';
            const valueField = cfg.valueField || 'opportunityId';
            const displayField = cfg.displayField || 'displayName';
            const emptyMsg = cfg.emptyStateMessage || 'No opportunities match your search.';
            const errorMsg = cfg.errorStateMessage
              || "Couldn't reach MSX right now. You can type the Opportunity ID manually.";
            // WI 1278277 (fast-query prefetch): when the backend enables it (MsxConfiguration:UseFastQuery),
            // pre-populate the dropdown with the latest top-N on focus. Purely additive — type-to-search below
            // is unchanged; typing takes over from the prefetched list.
            const prefetch = cfg.prefetch === true;

            const container: HTMLElement | null =
              (input.closest && input.closest('.form-group')) || input.parentElement;
            if (!container) continue;
            if (getComputedStyle(container).position === 'static') {
              container.style.position = 'relative';
            }

            // Fluent design tokens — matched to the SOS "TPID" tag-picker (autocomplete-search-
            // tagpicker in form-render.js) so the Opportunity field looks & behaves identically.
            const FONT = "'Segoe UI', 'Segoe UI Web (West European)', -apple-system, sans-serif";

            // Reuse the suggestions panel across injection ticks (idempotent).
            let panel = container.querySelector(':scope > .opp-suggest-panel') as HTMLElement | null;
            if (!panel) {
              panel = document.createElement('div');
              panel.className = 'opp-suggest-panel';
              panel.setAttribute('role', 'listbox');
              panel.style.cssText =
                'display:none; position:absolute; z-index:1000; max-height:280px; overflow-y:auto; ' +
                'background:#fff; border:1px solid #e1dfdd; border-radius:4px; ' +
                'box-shadow:0 3.2px 7.2px rgba(0,0,0,0.13),0 0.6px 1.8px rgba(0,0,0,0.11); ' +
                `font-family:${FONT}; font-size:14px; color:#201f1e; padding:4px 0; box-sizing:border-box;`;
              container.appendChild(panel);
            }
            const panelEl = panel;

            // Anchor the panel directly beneath the input using live geometry, so it never
            // overlaps the next field regardless of the container's internal layout.
            const positionPanel = () => {
              try {
                const ir = input.getBoundingClientRect();
                const cr = container.getBoundingClientRect();
                panelEl.style.top = `${ir.bottom - cr.top + 2}px`;
                panelEl.style.left = `${ir.left - cr.left}px`;
                panelEl.style.width = `${ir.width}px`;
              } catch { /* best-effort */ }
            };
            const hidePanel = () => { panelEl.style.display = 'none'; panelEl.textContent = ''; };

            // Explicit selections are tracked on the web-component instance (not formValues,
            // which the component overwrites on every keystroke) so the chip survives re-renders.
            const selStore: Record<string, { value: string; display: string }> =
              (formBuilder as any).__oppSelections || ((formBuilder as any).__oppSelections = {});
            const getFormVal = () =>
              String(((formBuilder as any).formValues || {})[field.id] ?? '').trim();

            const removeChipHost = () => {
              const h = container.querySelector(':scope > .opp-chip-host');
              if (h) h.remove();
            };
            const showInput = () => { if (input.style.display === 'none') input.style.display = ''; };

            const clearSelection = () => {
              delete selStore[field.id];
              commitValue(input, field.id, '');
              removeChipHost();
              input.style.display = '';
              try { input.focus(); } catch { /* best-effort */ }
            };

            // Render the selected value as a removable tag inside a bordered box that stands in
            // for the (hidden) text field — the ".selected-option" chip look from the TPID picker.
            const ensureChipHost = (value: string, display: string) => {
              const existing = container.querySelector(':scope > .opp-chip-host') as HTMLElement | null;
              if (existing && existing.getAttribute('data-value') === value) { input.style.display = 'none'; return; }
              if (existing) existing.remove();
              const host = document.createElement('div');
              host.className = 'opp-chip-host';
              host.setAttribute('data-value', value);
              host.style.cssText =
                'display:flex; flex-wrap:wrap; align-items:center; gap:5px; width:100%; ' +
                'min-height:34px; padding:5px 8px; box-sizing:border-box; ' +
                'border:2px solid #cccccc; border-radius:4px; background:#fff;';
              const chip = document.createElement('div');
              chip.className = 'opp-selected-chip';
              chip.style.cssText =
                'display:flex; align-items:center; background:#f0f0f0; padding:5px 10px; ' +
                `border-radius:5px; max-width:100%; font-family:${FONT}; font-size:14px; color:#201f1e; word-break:break-word;`;
              const label = document.createElement('span');
              label.textContent = display || value;
              label.title = display || value;
              const remove = document.createElement('button');
              remove.type = 'button';
              remove.setAttribute('aria-label', 'Remove selected opportunity');
              remove.textContent = '\u00D7';
              remove.style.cssText =
                'margin-left:10px; background:none; border:none; cursor:pointer; ' +
                'font-size:16px; line-height:1; color:#605e5c; padding:0;';
              remove.addEventListener('click', (e) => { e.preventDefault(); e.stopPropagation(); clearSelection(); });
              chip.appendChild(label);
              chip.appendChild(remove);
              host.appendChild(chip);
              if (input.parentElement) input.parentElement.insertBefore(host, input.nextSibling);
              else container.appendChild(host);
              input.style.display = 'none';
            };

            // Reconcile the visible UI with the tracked selection — runs every injection tick so
            // the chip is restored after the web component's internal re-renders.
            const syncSelectionUI = () => {
              const sel = selStore[field.id];
              if (sel && getFormVal() === sel.value) {
                hidePanel();
                ensureChipHost(sel.value, sel.display);
              } else {
                if (sel && getFormVal() !== sel.value) delete selStore[field.id];
                removeChipHost();
                showInput();
              }
            };

            const selectOption = (value: string, display: string) => {
              selStore[field.id] = { value, display };
              commitValue(input, field.id, value);
              hidePanel();
              ensureChipHost(value, display);
            };

            const allowFreeText = cfg.fallbackToFreeText !== false;

            // Rotating loading spinner (Web Animations API works across the shadow boundary,
            // so no @keyframes injection into the shadow root is needed).
            const makeSpinner = (): HTMLElement => {
              const s = document.createElement('span');
              s.style.cssText =
                'display:inline-block; width:14px; height:14px; margin-left:auto; flex:0 0 auto; ' +
                'border:2px solid #c8c6c4; border-top-color:#0078d4; border-radius:50%;';
              try { s.animate([{ transform: 'rotate(0deg)' }, { transform: 'rotate(360deg)' }], { duration: 800, iterations: Infinity }); } catch { /* best-effort */ }
              return s;
            };

            // The typed term echoed as a highlighted first row (blue left accent + tinted bg),
            // exactly like the SOS TPID picker. Clickable to commit the raw text (free-text mode).
            const buildHeaderRow = (term: string, loading: boolean): HTMLElement => {
              const h = document.createElement('div');
              h.className = 'opp-suggest-header';
              h.style.cssText =
                'display:flex; align-items:center; gap:8px; padding:0 12px; min-height:36px; ' +
                'background:#eff6fc; border-left:3px solid #0078d4; box-sizing:border-box; ' +
                (allowFreeText ? 'cursor:pointer;' : 'cursor:default;');
              const t = document.createElement('span');
              t.textContent = term;
              t.style.cssText = 'overflow:hidden; text-overflow:ellipsis; white-space:nowrap;';
              h.appendChild(t);
              if (loading) h.appendChild(makeSpinner());
              if (allowFreeText) {
                h.addEventListener('mousedown', (e) => { e.preventDefault(); selectOption(term, term); });
              }
              return h;
            };

            // Show the blue term row + spinner immediately while the request is in flight.
            const showLoading = (term: string) => {
              panelEl.textContent = '';
              panelEl.appendChild(buildHeaderRow(term, true));
              positionPanel();
              panelEl.style.display = 'block';
            };

            const renderOptions = (items: any[], term: string, showHeader = true) => {
              panelEl.textContent = '';
              if (showHeader) panelEl.appendChild(buildHeaderRow(term, false));
              items.forEach((item) => {
                const value = String(item?.[valueField] ?? '');
                const display = String(item?.[displayField] ?? value);
                if (!value) return;
                // Show "opportunityId | name". Truncate to the field width with an ellipsis and a
                // native tooltip (title) carrying the full text so no information is lost.
                const label = display && display !== value ? `${value} | ${display}` : value;
                const opt = document.createElement('div');
                opt.setAttribute('role', 'option');
                opt.title = label;
                opt.style.cssText = 'padding:0 12px; min-height:36px; display:flex; align-items:center; cursor:pointer;';
                const text = document.createElement('span');
                text.textContent = label;
                text.style.cssText = 'overflow:hidden; text-overflow:ellipsis; white-space:nowrap; flex:1 1 auto; min-width:0;';
                opt.appendChild(text);
                opt.addEventListener('mouseenter', () => { opt.style.background = '#f3f2f1'; });
                opt.addEventListener('mouseleave', () => { opt.style.background = '#fff'; });
                opt.addEventListener('mousedown', (e) => {
                  e.preventDefault(); // keep focus so blur doesn't cancel the click
                  selectOption(value, label);
                });
                panelEl.appendChild(opt);
              });
              positionPanel();
              panelEl.style.display = 'block';
            };

            // Restore/collapse the chip for the current selection on every tick.
            syncSelectionUI();

            // Bind the typeahead listeners exactly once per input element.
            if (input.getAttribute('data-opp-search-bound') !== '1') {
              input.setAttribute('data-opp-search-bound', '1');
              // WI 1278277: server-side filtered search. A selective text filter lets MSX/Dataverse
              // narrow before the expensive access-team join (~260ms), whereas an unfiltered preload
              // must join+sort the user's whole opportunity set (~15s). So we do NOT preload — each
              // query (debounced, 3+ chars) hits the backend with the search term.
              const showEmpty = (term: string) => {
                panelEl.textContent = '';
                panelEl.appendChild(buildHeaderRow(term, false));
                const msg = document.createElement('div');
                msg.style.cssText = 'padding:8px 12px; color:#605e5c;';
                msg.textContent = emptyMsg;
                panelEl.appendChild(msg);
                positionPanel();
                panelEl.style.display = 'block';
              };
              // Distinct warning (amber) shown when the source (MSX) is unavailable — the backend
              // returns HTTP 5xx in that case (vs a 200 empty list for a genuine no-match).
              const showError = (term: string) => {
                panelEl.textContent = '';
                panelEl.appendChild(buildHeaderRow(term, false));
                const msg = document.createElement('div');
                msg.style.cssText = 'padding:8px 12px; color:#8a5000; background:#fff4ce;';
                msg.textContent = errorMsg;
                panelEl.appendChild(msg);
                positionPanel();
                panelEl.style.display = 'block';
              };
              // Interpret a response: an HTTP 5xx/4xx (or a non-array body) is an error; a 200 with
              // an array (possibly empty) is a genuine result set.
              const readItems = (resp: any): { error: boolean; items: any[] } => {
                const status = resp && typeof resp.status === 'number' ? resp.status : undefined;
                const data = resp?.data ?? resp;
                const items = Array.isArray(data)
                  ? data
                  : (Array.isArray(data?.items) ? data.items : null);
                const error = (status !== undefined && status >= 400) || items === null;
                return { error, items: items || [] };
              };
              // Debounced server-side search. seq guards against out-of-order responses (a slower
              // earlier request must not overwrite the panel after a newer one has resolved).
              let debounceTimer: any = null;
              let seq = 0;
              const runSearch = async (term: string) => {
                const mine = ++seq;
                showLoading(term);
                try {
                  const sep = cfg.endpoint.includes('?') ? '&' : '?';
                  const url = `${cfg.endpoint}${sep}${searchParam}=${encodeURIComponent(term)}`;
                  const resp = await getExternalConsumptionAPI(url, (platformContext as any).authClient);
                  if (mine !== seq) return; // a newer keystroke superseded this request
                  // The user may have selected/cleared while the request was in flight.
                  if (selStore[field.id]) return;
                  if (String(input.value || '').trim() !== term) return;
                  const { error, items } = readItems(resp);
                  if (error) { showError(term); return; }
                  if (items.length === 0) showEmpty(term); else renderOptions(items, term);
                } catch {
                  if (mine !== seq) return;
                  showError(term);
                }
              };
              // WI 1278277 (fast-query prefetch): when the server enables prefetch (MsxConfiguration:UseFastQuery),
              // fetch the latest top-N ONCE at form load and keep them in memory. Typing then filters this set
              // client-side into the same dropdown; a term with no local match still falls back to the live
              // server search. Purely additive — the type-to-search path is otherwise unchanged.
              let prefetchItems: any[] | null = null;
              let prefetchLoading = false;
              // Show the full prefetched top-N in the dropdown, but only while the field is empty/focused.
              const showAllPrefetch = () => {
                if (!prefetch || !prefetchItems || !prefetchItems.length) return;
                if (String(input.value || '').trim().length > 0) return;
                if (selStore[field.id]) return;
                if ((formBuilder as any).__oppPrefilled?.has(field.id.toLowerCase())) return;
                seq++; renderOptions(prefetchItems, '', false);
              };
              const startPrefetch = async (showOnDone = false) => {
                if (prefetchItems !== null) { if (showOnDone) showAllPrefetch(); return; }
                if (prefetchLoading) return;
                prefetchLoading = true;
                try {
                  const sep = cfg.endpoint.includes('?') ? '&' : '?';
                  const url = `${cfg.endpoint}${sep}${searchParam}=`;
                  const resp = await getExternalConsumptionAPI(url, (platformContext as any).authClient);
                  const { error, items } = readItems(resp);
                  prefetchItems = error ? [] : items;
                } catch {
                  prefetchItems = prefetchItems || [];
                } finally {
                  prefetchLoading = false;
                }
                if (showOnDone) showAllPrefetch();
              };
              const filterPrefetch = (term: string): any[] => {
                if (!prefetchItems || !prefetchItems.length) return [];
                const t = term.toLowerCase();
                return prefetchItems.filter((i: any) => {
                  const v = i?.[cfg.valueField] != null ? String(i[cfg.valueField]).toLowerCase() : '';
                  const d = i?.[cfg.displayField] != null ? String(i[cfg.displayField]).toLowerCase() : '';
                  return v.indexOf(t) !== -1 || d.indexOf(t) !== -1;
                });
              };
              input.addEventListener('input', () => {
                // Prefilled fields behave as plain textboxes — never open the search dropdown.
                if ((formBuilder as any).__oppPrefilled?.has(field.id.toLowerCase())) return;
                if (selStore[field.id]) return; // a selection is active (input hidden) — ignore
                const term = String(input.value || '').trim();
                if (debounceTimer) { clearTimeout(debounceTimer); debounceTimer = null; }
                // Fast-query prefetch: an empty field shows the full prefetched top-N in the same dropdown.
                if (term.length === 0 && prefetch) { seq++; showAllPrefetch(); return; }
                // Only query after minChars (default 3), debounced to avoid a call per keystroke.
                if (term.length < minChars) { seq++; hidePanel(); return; }
                // Fast-query prefetch: filter the top-N fetched at load into the same dropdown. Only a
                // term with NO local match falls through to the live server search below.
                if (prefetch && prefetchItems && prefetchItems.length) {
                  const pf = filterPrefetch(term);
                  if (pf.length) { seq++; renderOptions(pf, term, false); return; }
                }
                debounceTimer = setTimeout(() => { void runSearch(term); }, debounceMs);
              });
              // Fast-query prefetch: on focus of an empty field, show the full prefetched top-N right away.
              input.addEventListener('focus', () => {
                if (!prefetch) return;
                if ((formBuilder as any).__oppPrefilled?.has(field.id.toLowerCase())) return;
                if (selStore[field.id]) return;
                if (String(input.value || '').trim().length > 0) return;
                void startPrefetch(true);
              });
              input.addEventListener('blur', () => { setTimeout(hidePanel, 150); });
              // Fast-query prefetch mode: warm the top-N at form load so it's ready the moment the user types.
              if (prefetch
                  && !(formBuilder as any).__oppPrefilled?.has(field.id.toLowerCase())
                  && !selStore[field.id]) {
                void startPrefetch();
              }
            }
          }

          // Keep the injected typeahead/chip alive across the web component's internal re-renders.
          if (!(formBuilder as any).__oppSearchObserver) {
            let scheduled = false;
            const reinject = () => {
              if (scheduled) return;
              scheduled = true;
              requestAnimationFrame(() => { scheduled = false; injectOpportunitySearch(); });
            };
            try {
              const obs = new MutationObserver(reinject);
              obs.observe(innerShadow, { childList: true, subtree: true });
              (formBuilder as any).__oppSearchObserver = obs;
            } catch { /* best-effort */ }
          }
        };
        // Same cadence as the other injections — inner shadow DOM renders asynchronously.
        injectOpportunitySearch();
        requestAnimationFrame(injectOpportunitySearch);
        setTimeout(injectOpportunitySearch, 300);
        setTimeout(injectOpportunitySearch, 800);
        setTimeout(injectOpportunitySearch, 1500);

        // ============================================================================
        // WI 1281118: Client-side type-to-search for large option dropdowns (Competitor &
        // Competitor Product). The copilot form-builder renders these as a native
        // <fluent-select> with no filtering, so scrolling a long fetched list is painful.
        // This is purely a UI enhancement — the options are still fetched/populated exactly
        // as before (no API/data/backend change). Using the same shadow-DOM injection
        // technique as the Opportunity ID typeahead, we hide the native select (kept as the
        // value carrier), show a click-to-open panel with a filter box that narrows +
        // highlights the EXISTING options, a "No records found" empty state, and strict
        // selection only (no free text). Selecting writes the value back to the native
        // select and fires change, so the existing Competitor→Product cascade keeps working.
        // Idempotent + re-injects on the component's internal re-renders (cascade).
        // ============================================================================
        const SEARCHABLE_DROPDOWN_FIELD_IDS = new Set(['competitor', 'competitorproduct']);
        const injectDropdownSearch = () => {
          const targetFields = (fields || []).filter(
            (f: any) => f?.type === 'dropdown' && SEARCHABLE_DROPDOWN_FIELD_IDS.has(String(f?.id || '').toLowerCase())
          );
          if (targetFields.length === 0) return;

          const outerShadow = formElement.shadowRoot;
          if (!outerShadow) return;
          const formBuilder = outerShadow.querySelector('dynamic-copilot-form-builder') ||
                              outerShadow.querySelector('dynamic-form-builder') ||
                              outerShadow.querySelector('dynamic-form-builder-standalone');
          if (!formBuilder) return;
          const innerShadow: ShadowRoot | null = (formBuilder as any).shadowRoot;
          if (!innerShadow) return;

          const FONT = "'Segoe UI', 'Segoe UI Web (West European)', -apple-system, sans-serif";

          const findSelect = (fieldId: string): any => {
            const all = Array.from(innerShadow.querySelectorAll('fluent-select, select')) as any[];
            const target = fieldId.toLowerCase();
            return all.find((cand) => {
              const id = (cand.id || '').toLowerCase();
              const nm = (cand.getAttribute?.('name') || cand.name || '').toLowerCase();
              return id === target || nm === target;
            }) || null;
          };

          for (const field of targetFields) {
            const fieldId = String(field.id);
            // Re-resolve the select on every access so a cascade that REPLACES the child
            // (Competitor Product) select element never leaves the handlers pointing at a
            // stale node.
            const getSel = () => findSelect(fieldId);
            const sel = getSel();
            if (!sel) continue;

            // Read the live options straight from the rendered select so cascade-updated
            // Competitor Product lists are always current. No preload, no fetch.
            const readOptions = (): Array<{ value: string; text: string }> => {
              const s = getSel();
              if (!s) return [];
              const opts = Array.from(s.querySelectorAll('fluent-option, option')) as any[];
              return opts
                .map((o) => {
                  const value = String(o.value ?? o.getAttribute?.('value') ?? o.textContent ?? '').trim();
                  const text = String(o.textContent ?? value).trim();
                  return { value, text };
                })
                .filter((o) => o.value !== '' && o.text !== '' && o.text.toLowerCase() !== 'select...');
            };

            const currentText = (): string => {
              const s = getSel();
              const v = String(s?.value ?? '');
              if (!v) return '';
              const match = readOptions().find((o) => o.value === v);
              return match ? match.text : v;
            };

            const container: HTMLElement | null =
              (sel.closest && sel.closest('.form-group')) || sel.parentElement;
            if (!container) continue;
            if (getComputedStyle(container).position === 'static') {
              container.style.position = 'relative';
            }

            // Reuse the combo + panel across ticks (idempotent).
            let combo = container.querySelector(':scope > .ta-combo') as HTMLElement | null;
            let panel = container.querySelector(':scope > .ta-panel') as HTMLElement | null;

            if (!combo) {
              combo = document.createElement('div');
              combo.className = 'ta-combo';
              combo.setAttribute('role', 'combobox');
              combo.setAttribute('aria-haspopup', 'listbox');
              combo.setAttribute('tabindex', '0');
              combo.style.cssText =
                'display:flex; align-items:center; gap:8px; width:100%; min-height:34px; ' +
                'padding:4px 10px; box-sizing:border-box; border:2px solid #cccccc; ' +
                'border-radius:4px; background:#fff; cursor:pointer; ' +
                `font-family:${FONT}; font-size:14px; color:#201f1e;`;
              const labelSpan = document.createElement('span');
              labelSpan.className = 'ta-combo-label';
              labelSpan.style.cssText = 'flex:1 1 auto; overflow:hidden; text-overflow:ellipsis; white-space:nowrap;';
              const caret = document.createElement('span');
              caret.textContent = '\u25BC';
              caret.style.cssText = 'flex:0 0 auto; font-size:10px; color:#605e5c;';
              combo.appendChild(labelSpan);
              combo.appendChild(caret);
              // Insert right after the (hidden) native select.
              if (sel.parentElement) sel.parentElement.insertBefore(combo, sel.nextSibling);
              else container.appendChild(combo);
            }
            const comboEl = combo;
            const labelSpan = comboEl.querySelector('.ta-combo-label') as HTMLElement;

            if (!panel) {
              panel = document.createElement('div');
              panel.className = 'ta-panel';
              panel.setAttribute('role', 'listbox');
              panel.style.cssText =
                'display:none; position:absolute; z-index:1000; max-height:280px; overflow-y:auto; ' +
                'background:#fff; border:1px solid #e1dfdd; border-radius:4px; ' +
                'box-shadow:0 3.2px 7.2px rgba(0,0,0,0.13),0 0.6px 1.8px rgba(0,0,0,0.11); ' +
                `font-family:${FONT}; font-size:14px; color:#201f1e; box-sizing:border-box;`;
              container.appendChild(panel);
            }
            const panelEl = panel;

            // Hide the native select; it stays in the DOM as the value carrier / submit source.
            sel.style.display = 'none';

            // Reflect the current selection (also covers cascade resets that clear the value).
            const placeholder = String((field as any).placeholder || 'Select...');
            const refreshLabel = () => {
              const t = currentText();
              labelSpan.textContent = t || placeholder;
              labelSpan.style.color = t ? '#201f1e' : '#605e5c';
            };
            refreshLabel();

            const positionPanel = () => {
              try {
                const cr = comboEl.getBoundingClientRect();
                const pr = container.getBoundingClientRect();
                panelEl.style.top = `${cr.bottom - pr.top + 2}px`;
                panelEl.style.left = `${cr.left - pr.left}px`;
                panelEl.style.width = `${cr.width}px`;
              } catch { /* best-effort */ }
            };

            const hidePanel = () => { panelEl.style.display = 'none'; panelEl.textContent = ''; };

            // Strict select only: write the chosen option's value back to the native select
            // and fire input/change so the form-builder captures it and the cascade runs.
            const selectValue = (value: string) => {
              const s = getSel();
              if (s) {
                try {
                  s.value = value;
                  s.dispatchEvent(new Event('input', { bubbles: true, composed: true }));
                  s.dispatchEvent(new Event('change', { bubbles: true, composed: true }));
                } catch { /* best-effort */ }
              }
              refreshLabel();
              hidePanel();
            };

            // Build the filterable list; highlights the matched substring; no free text.
            const renderList = (term: string) => {
              const rows = panelEl.querySelector('.ta-rows') as HTMLElement | null;
              if (!rows) return;
              rows.textContent = '';
              const q = term.trim().toLowerCase();
              const all = readOptions();
              const matched = q
                ? all.filter((o) => o.text.toLowerCase().includes(q) || o.value.toLowerCase().includes(q))
                : all;
              if (matched.length === 0) {
                const empty = document.createElement('div');
                empty.style.cssText = 'padding:10px 12px; color:#605e5c;';
                empty.textContent = 'No records found';
                rows.appendChild(empty);
                return;
              }
              const selectedVal = String(getSel()?.value ?? '');
              matched.forEach((o) => {
                const opt = document.createElement('div');
                opt.setAttribute('role', 'option');
                opt.title = o.text;
                const isSel = o.value === selectedVal;
                opt.style.cssText =
                  'padding:0 12px; min-height:34px; display:flex; align-items:center; cursor:pointer; ' +
                  (isSel ? 'background:#f3f2f1; font-weight:600;' : '');
                const text = document.createElement('span');
                text.style.cssText = 'overflow:hidden; text-overflow:ellipsis; white-space:nowrap; flex:1 1 auto; min-width:0;';
                const idx = q ? o.text.toLowerCase().indexOf(q) : -1;
                if (idx >= 0) {
                  text.appendChild(document.createTextNode(o.text.slice(0, idx)));
                  const mark = document.createElement('span');
                  mark.textContent = o.text.slice(idx, idx + q.length);
                  mark.style.cssText = 'background:#fff4ce; font-weight:600;';
                  text.appendChild(mark);
                  text.appendChild(document.createTextNode(o.text.slice(idx + q.length)));
                } else {
                  text.textContent = o.text;
                }
                opt.appendChild(text);
                opt.addEventListener('mouseenter', () => { if (!isSel) opt.style.background = '#f3f2f1'; });
                opt.addEventListener('mouseleave', () => { if (!isSel) opt.style.background = '#fff'; });
                opt.addEventListener('mousedown', (e) => { e.preventDefault(); selectValue(o.value); });
                rows.appendChild(opt);
              });
            };

            const openPanel = () => {
              panelEl.textContent = '';
              // Sticky search box.
              const searchWrap = document.createElement('div');
              searchWrap.style.cssText =
                'position:sticky; top:0; background:#fff; padding:8px; border-bottom:1px solid #edebe9; box-sizing:border-box;';
              const search = document.createElement('input');
              search.type = 'text';
              search.placeholder = 'Type to search\u2026';
              search.setAttribute('aria-label', `Search ${field.label || fieldId}`);
              search.style.cssText =
                'width:100%; box-sizing:border-box; padding:6px 8px; border:1px solid #605e5c; ' +
                `border-radius:4px; font-family:${FONT}; font-size:14px; color:#201f1e; outline:none;`;
              searchWrap.appendChild(search);
              panelEl.appendChild(searchWrap);
              const rows = document.createElement('div');
              rows.className = 'ta-rows';
              panelEl.appendChild(rows);
              positionPanel();
              panelEl.style.display = 'block';
              renderList('');
              search.addEventListener('input', () => renderList(search.value));
              search.addEventListener('keydown', (e) => {
                if (e.key === 'Escape') { e.preventDefault(); hidePanel(); try { comboEl.focus(); } catch { /* best-effort */ } }
              });
              setTimeout(() => { try { search.focus(); } catch { /* best-effort */ } }, 0);
            };

            const togglePanel = () => {
              if (panelEl.style.display === 'block') hidePanel(); else openPanel();
            };

            if (comboEl.getAttribute('data-ta-bound') !== '1') {
              comboEl.setAttribute('data-ta-bound', '1');
              comboEl.addEventListener('click', (e) => { e.preventDefault(); e.stopPropagation(); togglePanel(); });
              comboEl.addEventListener('keydown', (e) => {
                if (e.key === 'Enter' || e.key === ' ' || e.key === 'ArrowDown') { e.preventDefault(); openPanel(); }
                else if (e.key === 'Escape') { hidePanel(); }
              });
              // Close when clicking anywhere outside the combo/panel (composedPath crosses shadow roots).
              document.addEventListener('mousedown', (e) => {
                const path = (e as any).composedPath ? (e as any).composedPath() : [];
                if (!path.includes(comboEl) && !path.includes(panelEl)) hidePanel();
              });
            }
          }

          // Re-apply after the component's internal re-renders (e.g. Competitor→Product cascade).
          if (!(formBuilder as any).__taSearchObserver) {
            let scheduled = false;
            const reinject = () => {
              if (scheduled) return;
              scheduled = true;
              requestAnimationFrame(() => { scheduled = false; injectDropdownSearch(); });
            };
            try {
              const obs = new MutationObserver(reinject);
              obs.observe(innerShadow, { childList: true, subtree: true });
              (formBuilder as any).__taSearchObserver = obs;
            } catch { /* best-effort */ }
          }
        };
        // Same cadence as the other injections — inner shadow DOM renders asynchronously.
        injectDropdownSearch();
        requestAnimationFrame(injectDropdownSearch);
        setTimeout(injectDropdownSearch, 300);
        setTimeout(injectDropdownSearch, 800);
        setTimeout(injectDropdownSearch, 1500);

        // Follow DynamicForm.tsx pattern exactly: simple direct property assignment
        (formElement as any).context = platformContext; // ✅ Authentication context (like DynamicForm's context)
        
        // ✅ API configuration using same structure as DynamicForm.tsx
        (formElement as any).mcapsApiConfig = apiConfig;
        (formElement as any).apiConfig = apiConfig; // v1.0.3 compatibility: form-render uses apiConfig
        
        // ✅ Form schema/configuration (like DynamicForm's schema) - pass as JSON object
        (formElement as any).schema = {
          formFields: fields,
          fields: fields, // v1.0.3 compatibility: form-render expects schema.fields
          formTitle: formTitle || 'Dynamic Form',
          title: formTitle || 'Dynamic Form', // v1.0.3 compatibility: form-render expects schema.title
          formDescription: formDescription || '',
          formType: 'copilot-form',
          // Include formJson metadata if available (new API format)
          ...(formJson && {
            formKey: formJson.formKey,
            formName: formJson.formName,
            sessionId: formJson.sessionId,
            currentSubsectionKey: formJson.currentSubsectionKey,
            currentSubsectionTitle: formJson.currentSubsectionTitle,
            subsectionProgress: formJson.subsectionProgress,
            progress: formJson.progress
          })
        };
        
        // ✅ Use API JSON response as dynamicOptions with formJson integration
        (formElement as any).dynamicOptions = {
          mode: 'copilot',
          enableValidation: true,
          autoSubmit: false,
          isActiveForm: isActiveForm, // Whether this form can be submitted
          // Include persistent form values to restore previous entries
          persistentFormValues: persistentFormValues,
          // Include formJson data for advanced form handling
          ...(formJson && {
            formJson: formJson,
            collectedData: formJson.collectedData,
            completedSubsections: formJson.completedSubsections,
            navigationOptions: formJson.navigationOptions
          }),
          // Merge with any existing apiJsonResponse data
          ...(apiJsonResponse && typeof apiJsonResponse === 'object' ? apiJsonResponse : {})
        };

        // 🔥 Story 1249086: Pre-fill form fields from collectedData.
        // The web component doesn't bind formValues to <fluent-text-field> value attributes.
        // ============================================================================
        // PRE-FILL LOGIC: Populate form fields from collectedData (AI-extracted values)
        // ============================================================================
        // ARCHITECTURE:
        //   formElement = <dynamic-copilot-form-render> (outer wrapper)
        //     └── shadowRoot
        //           └── <dynamic-form-builder> (has formValues property)
        //                 └── shadowRoot
        //                       └── <fluent-text-field name="shortDescription"> (actual inputs)
        //
        // CHALLENGE: The web component's fluent-text-field for type="text" does NOT have
        // a .value binding to formValues (it exists for dropdowns but not text fields).
        // This is a bug in copilot_render.v1.0.3.js. Until fixed, we must:
        //   1. Set formValues on dynamic-form-builder (for form submission)
        //   2. Directly set .value on fluent-text-field DOM elements (for display)
        //   3. Re-apply after re-renders (form re-renders clear DOM values)
        // ============================================================================
        
        if (!prefillAppliedRef.current && formJson?.collectedData && Object.keys(formJson.collectedData).length > 0) {
          prefillAppliedRef.current = true;

          // Helper: Find the inner dynamic-copilot-form-builder component
          const getFormBuilder = (): Element | null => {
            if (formElement.shadowRoot) {
              const builder = formElement.shadowRoot.querySelector('dynamic-copilot-form-builder') ||
                              formElement.shadowRoot.querySelector('dynamic-form-builder');
              if (builder) return builder;
            }
            if ((formElement as any).formValues !== undefined) {
              return formElement;
            }
            return null;
          };

          // Helper: Deep query into shadow DOM to find all matching elements
          const deepQueryAll = (root: Element | ShadowRoot, selector: string): Element[] => {
            const results: Element[] = [];
            try {
              results.push(...Array.from(root.querySelectorAll(selector)));
            } catch (e) {
              // Some roots don't support querySelectorAll
            }
            const allElements = root.querySelectorAll('*');
            for (const el of Array.from(allElements)) {
              if (el.shadowRoot) {
                results.push(...deepQueryAll(el.shadowRoot, selector));
              }
            }
            return results;
          };

          // Main pre-fill function: Set formValues AND DOM values
          const applyPreFill = (): number => {
            const formBuilder = getFormBuilder();
            if (!formBuilder) return 0;

            // Set formValues on the component (for form submission)
            const currentFormValues = (formBuilder as any).formValues || {};
            const newFormValues = { ...currentFormValues, ...formJson.collectedData, ...persistentFormValues };
            (formBuilder as any).formValues = newFormValues;

            // Find and set DOM values on actual input elements
            const searchRoot = formBuilder.shadowRoot || formBuilder;
            const selector = 'fluent-text-field, fluent-text-area, fluent-select, fluent-combobox, input, textarea, select';
            const allInputs = deepQueryAll(searchRoot as any, selector);
            
            // Build a map of inputs by id/name for quick lookup
            const inputMap = new Map<string, Element>();
            for (const input of allInputs) {
              const id = (input as any).id?.toLowerCase();
              const name = (input as any).name?.toLowerCase();
              if (id) inputMap.set(id, input);
              if (name && name !== id) inputMap.set(name, input);
            }

            let foundCount = 0;
            
            // Identify cascade child fields to defer
            const cascadeChildIds = new Set<string>();
            if (formJson.fields) {
              for (const field of formJson.fields) {
                if (field.cascadeConfig?.parentFieldId) {
                  cascadeChildIds.add(field.id.toLowerCase());
                }
              }
            }

            // Feedback: prefilled searchable (dataSource) fields — e.g. Opportunity ID — must render
            // as a PLAIN TEXTBOX carrying the value, NOT the typeahead dropdown. We record the field
            // id in the shared __oppPrefilled set so injectOpportunitySearch skips enhancing it (and
            // tears down any enhancement already applied). We set the value WITHOUT dispatching
            // 'input' so no search/dropdown is triggered. Unfilled dataSource fields are untouched
            // here and keep their normal searchable-dropdown behaviour.
            const dataSourceFieldIds = new Set<string>();
            if (formJson.fields) {
              for (const field of formJson.fields) {
                if (field?.dataSource?.endpoint) {
                  dataSourceFieldIds.add(field.id.toLowerCase());
                }
              }
            }
            const oppSelStore: Record<string, { value: string; display: string }> =
              (formBuilder as any).__oppSelections || ((formBuilder as any).__oppSelections = {});
            const oppPrefilledSet: Set<string> =
              (formBuilder as any).__oppPrefilled || ((formBuilder as any).__oppPrefilled = new Set<string>());

            const deferredFieldData: Array<{ fieldId: string; value: any }> = [];

            for (const [fieldId, value] of Object.entries(formJson.collectedData)) {
              if (fieldId.startsWith('__')) continue;
              
              const fieldIdLower = fieldId.toLowerCase();
              const input = inputMap.get(fieldIdLower);
              
              if (input) {
                if (dataSourceFieldIds.has(fieldIdLower)) {
                  // Prefilled searchable field (Opportunity ID) → plain textbox. Mark it prefilled
                  // so injectOpportunitySearch renders it as a normal text input (no dropdown), drop
                  // any prior chip selection, and set the value WITHOUT dispatching 'input' (which
                  // would trigger the typeahead search). formValues (set above) covers submission.
                  const valStr = value == null ? '' : String(value);
                  oppPrefilledSet.add(fieldIdLower);
                  delete oppSelStore[fieldId];
                  (input as any).value = valStr;
                  if ((input as any).currentValue !== undefined) {
                    (input as any).currentValue = valStr;
                  }
                  foundCount++;
                } else if (cascadeChildIds.has(fieldIdLower)) {
                  // Defer cascade children - store ID only, NOT DOM reference (goes stale after cascade)
                  deferredFieldData.push({ fieldId: fieldIdLower, value });
                } else {
                  // Only set value and dispatch events if value is different (prevents flicker)
                  const currentValue = (input as any).value;
                  if (currentValue !== value) {
                    (input as any).value = value;
                    if ((input as any).currentValue !== undefined) {
                      (input as any).currentValue = value as string;
                    }
                    input.dispatchEvent(new Event('input', { bubbles: true, composed: true }));
                    input.dispatchEvent(new Event('change', { bubbles: true, composed: true }));
                  }
                  foundCount++;
                }
              }
            }

            // Handle deferred cascade fields - re-query DOM after parent cascade settles
            if (deferredFieldData.length > 0) {
              setTimeout(() => {
                // Re-query DOM fresh - parent cascade may have re-rendered child dropdowns
                const freshBuilder = getFormBuilder();
                if (!freshBuilder) return;
                const freshRoot = freshBuilder.shadowRoot || freshBuilder;
                const freshInputs = deepQueryAll(freshRoot as any, selector);
                
                // Build fresh input map
                const freshInputMap = new Map<string, Element>();
                for (const inp of freshInputs) {
                  const id = (inp as any).id?.toLowerCase();
                  const name = (inp as any).name?.toLowerCase();
                  if (id) freshInputMap.set(id, inp);
                  if (name && name !== id) freshInputMap.set(name, inp);
                }
                
                for (const { fieldId, value } of deferredFieldData) {
                  const input = freshInputMap.get(fieldId);
                  if (input) {
                    const currentValue = (input as any).value;
                    if (currentValue !== value) {
                      (input as any).value = value;
                      if ((input as any).currentValue !== undefined) {
                        (input as any).currentValue = value as string;
                      }
                      input.dispatchEvent(new Event('input', { bubbles: true, composed: true }));
                      input.dispatchEvent(new Event('change', { bubbles: true, composed: true }));
                    }
                  }
                }
              }, 150); // Longer delay to ensure cascade settles
              foundCount += deferredFieldData.length;
            }

            // Trigger re-render to ensure formValues take effect
            if (typeof (formBuilder as any).requestUpdate === 'function') {
              (formBuilder as any).requestUpdate();
            }

            return foundCount;
          };

          // Retry mechanism:
          // - Only apply prefill when form reports formReady === true
          // - The value-check guard inside applyPreFill prevents redundant events
          let attempts = 0;
          const maxAttempts = 10;
          
          const tryApply = () => {
            attempts++;
            const formBuilder = getFormBuilder();
            const isReady = formBuilder && (formBuilder as any).formReady === true;
            
            if (isReady) {
              applyPreFill();
            } else if (attempts < maxAttempts) {
              setTimeout(tryApply, 300);
            } else {
              // Fix #4: Log warning instead of silently dropping prefill
              console.warn('[FormMessage] Prefill abandoned: formReady never became true after', maxAttempts, 'attempts (', maxAttempts * 300, 'ms)');
            }
          };
          
          tryApply();
          
        } else if (persistentFormValues && Object.keys(persistentFormValues).length > 0) {
          const formBuilder = formElement.shadowRoot?.querySelector('dynamic-copilot-form-builder') || formElement.shadowRoot?.querySelector('dynamic-form-builder') || formElement;
          (formBuilder as any).formValues = { ...(formBuilder as any).formValues, ...persistentFormValues };
          if (typeof (formBuilder as any).requestUpdate === 'function') {
            (formBuilder as any).requestUpdate();
          }
        }
        if (!(formElement as any)._listenersAdded) {
          // ============================================================================
          // Bug 1272996: Client-side validation via submit-button click interception.
          // The web component does NOT enforce validation.pattern from field JSON AND
          // it sets its internal "submitting" state BEFORE dispatching the custom event.
          // So we intercept the submit button CLICK in capture phase — this fires
          // BEFORE the web component's own click handler, preventing "Submitted" state.
          // ============================================================================
          const patternFields = (fields || []).filter(
            (f: any) => f?.validation?.pattern && !f?.validation?.maxSizeMB
          );

          if (patternFields.length > 0) {
            const outerShadow = formElement.shadowRoot;
            const formBuilder = outerShadow?.querySelector('dynamic-copilot-form-builder') ||
              outerShadow?.querySelector('dynamic-form-builder');
            const innerShadow = formBuilder?.shadowRoot;
            const targetRoot = innerShadow || outerShadow;

            // --- A) Add blur-time validation on each pattern field input ---
            if (targetRoot) {
              for (const field of patternFields) {
                const input = targetRoot.querySelector(`[name="${field.id}"]`) ||
                  targetRoot.querySelector(`#${CSS.escape(field.id)}`);
                if (input && !(input as any)._patternValidatorAdded) {
                  const validateOnBlur = () => {
                    const val = (input as any).value || '';
                    const errorId = `pattern-error-${field.id}`;
                    // Remove previous error
                    targetRoot.querySelector(`#${CSS.escape(errorId)}`)?.remove();

                    if (val.trim() !== '') {
                      try {
                        const regex = new RegExp(field.validation.pattern);
                        if (!regex.test(val)) {
                          const container = input.closest('.form-group') || input.parentElement;
                          if (container) {
                            const errorDiv = document.createElement('div');
                            errorDiv.id = errorId;
                            errorDiv.setAttribute('role', 'alert');
                            errorDiv.style.cssText =
                              'color: #d13438; font-size: 12px; margin-top: 4px; font-weight: 600;';
                            errorDiv.textContent = `⚠ ${field.validation.errorMessage || 'Invalid format.'}`;
                            container.appendChild(errorDiv);
                          }
                        }
                      } catch { /* invalid regex */ }
                    }
                  };
                  input.addEventListener('blur', validateOnBlur);
                  input.addEventListener('input', () => {
                    const errorId = `pattern-error-${field.id}`;
                    targetRoot.querySelector(`#${CSS.escape(errorId)}`)?.remove();
                  });
                  (input as any)._patternValidatorAdded = true;
                }
              }
            }

            // --- B) Intercept submit button click in CAPTURE phase ---
            // This fires BEFORE the web component's own handler, so we can
            // preventDefault + stopPropagation to prevent "Submitted" state.
            const submitRoot = innerShadow || outerShadow;
            if (submitRoot && !(formElement as any)._submitClickInterceptAdded) {
              submitRoot.addEventListener('click', (e: Event) => {
                const target = e.target as HTMLElement;
                if (!target) return;
                // Detect submit button by text content, type, or class
                const isSubmitBtn =
                  target.tagName === 'BUTTON' && (
                    target.getAttribute('type') === 'submit' ||
                    (target.textContent || '').trim().toLowerCase() === 'submit'
                  );
                // Also check fluent-button
                const isFluentSubmit = target.tagName === 'FLUENT-BUTTON' && 
                  (target.textContent || '').trim().toLowerCase() === 'submit';
                  
                if (!isSubmitBtn && !isFluentSubmit) return;

                // Collect current values from the DOM inputs
                const validationErrors: string[] = [];
                const root = innerShadow || outerShadow;
                if (!root) return;

                for (const field of patternFields) {
                  const inp = root.querySelector(`[name="${field.id}"]`) ||
                    root.querySelector(`#${CSS.escape(field.id)}`);
                  const val = inp ? ((inp as any).value || '') : '';
                  if (!val || val.trim() === '') continue;
                  try {
                    const regex = new RegExp(field.validation.pattern);
                    if (!regex.test(val)) {
                      validationErrors.push(
                        field.validation.errorMessage ||
                        `${field.label || field.id} has an invalid format.`
                      );
                      // Show inline error
                      const errorId = `pattern-error-${field.id}`;
                      root.querySelector(`#${CSS.escape(errorId)}`)?.remove();
                      const container = inp?.closest('.form-group') || inp?.parentElement;
                      if (container) {
                        const errorDiv = document.createElement('div');
                        errorDiv.id = errorId;
                        errorDiv.setAttribute('role', 'alert');
                        errorDiv.style.cssText =
                          'color: #d13438; font-size: 12px; margin-top: 4px; font-weight: 600;';
                        errorDiv.textContent = `⚠ ${field.validation.errorMessage || 'Invalid format.'}`;
                        container.appendChild(errorDiv);
                        setTimeout(() => errorDiv.remove(), 10000);
                      }
                    }
                  } catch { /* invalid regex */ }
                }

                if (validationErrors.length > 0) {
                  // BLOCK the click — prevents web component from entering "submitting" state
                  e.preventDefault();
                  e.stopImmediatePropagation();
                  e.stopPropagation();
                  console.warn('[Bug1272996] Submit BLOCKED at click level:', validationErrors);
                }
              }, true); // capture phase — fires BEFORE the web component's handler

              (formElement as any)._submitClickInterceptAdded = true;
            }
          }

          (formElement as any)._listenersAdded = true;
        }
      } else {
        // Like DynamicForm.tsx: keep checking until element and data are ready
        requestAnimationFrame(checkElementAndSetProperties);
      }
    };

    if (webComponentLoaded && fields && platformContext) {
      checkElementAndSetProperties();
    }
  }, [webComponentLoaded, fields, platformContext, formTitle, formDescription, apiConfig, apiJsonResponse, formJson, onFormSubmit, onShowNextSection, componentKey]);

  // const handleRetry = () => {
  //   setError(null);
  //   setIsLoading(true);
  //   setWebComponentLoaded(false);
  //   setComponentKey(Date.now()); // Force complete re-initialization
  //   loadCopilotRenderScript();
  // };

  // if (error) {
  //   return (
  //     <div className={styles.formContainer}>
  //       <div className={styles.errorMessage}>
  //         {error}
  //         <Button 
  //           className={styles.retryButton}
  //           appearance="primary" 
  //           size="small"
  //           onClick={handleRetry}
  //         >
  //           Retry
  //         </Button>
  //       </div>
  //     </div>
  //   );
  // }

  if (isLoading) {
    return (
      <div className={styles.formContainer}>
        <div className={styles.loadingOverlay}>
          <Spinner size="medium" label="Loading form..." />
        </div>
      </div>
    );
  }

  // if (!webComponentLoaded) {
  //   return (
  //     <div className={styles.formContainer}>
  //       <div className={styles.fallbackContainer}>
  //         <p>Dynamic forms are not available in this view.</p>
  //         <p>Please ensure the form rendering components are properly loaded.</p>
  //         <Button 
  //           className={styles.retryButton}
  //           appearance="primary" 
  //           size="small"
  //           onClick={handleRetry}
  //         >
  //           Try Again
  //         </Button>
  //       </div>
  //     </div>
  //   );
  // }

  return (
    <FocusTrapZone
      isClickableOutsideFocusTrap={true}
      forceFocusInsideTrap={false}
      focusPreviouslyFocusedInnerElement={true}
    >
      <div className={styles.formContainer}>
        <dynamic-copilot-form-render
          id={`dynamicCopilotFormRender_${componentKey}`}
          key={componentKey}
          ref={formRef}
          className={styles.webComponent}
          data-tabster='{"uncontrolled": {}}'
        />
      </div>
    </FocusTrapZone>
  );
};

export default FormMessage;